import React, { useState, useEffect } from 'react'
import {
  useGetQueueMonitoringDataQuery,
  useCancelQueueMutation,
} from '../store/api/queueMonitoringApi'
import { formatDateTimeEST } from '../utils/dateUtils'
import {
  RectangleStackIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  StopIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const QueueMonitoring: React.FC = () => {
  const [autoRefresh, setAutoRefresh] = useState(true)
  
  const {
    data: monitoringData,
    isLoading,
    error,
    refetch,
  } = useGetQueueMonitoringDataQuery(undefined, {
    pollingInterval: autoRefresh ? 5000 : 0, // Poll every 5 seconds if auto-refresh is enabled
  })

  const [cancelQueue, { isLoading: isCancelling }] = useCancelQueueMutation()

  // Auto-refresh handler
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        refetch()
      }, 5000)
      return () => clearInterval(interval)
    }
  }, [autoRefresh, refetch])

  const handleCancelQueue = async (queueId: number) => {
    if (!confirm('Are you sure you want to cancel this queue? This action cannot be undone.')) {
      return
    }

    try {
      await cancelQueue(queueId).unwrap()
      toast.success('Queue cancelled successfully')
      refetch()
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to cancel queue')
    }
  }

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A'
    if (seconds < 60) return `${seconds}s`
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
  }

  const getStatusBadge = (status: string) => {
    const statusColors = {
      QUEUED: 'bg-yellow-100 text-yellow-800',
      STARTED: 'bg-blue-100 text-blue-800',
      FINISHED: 'bg-green-100 text-green-800',
      CRASHED: 'bg-red-100 text-red-800',
      REJECTED: 'bg-gray-100 text-gray-800',
      CANCELLED: 'bg-gray-100 text-gray-800',
    }
    return (
      <span
        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          statusColors[status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800'
        }`}
      >
        {status}
      </span>
    )
  }

  const getMotiveLabel = (motive: string) => {
    const labels: Record<string, string> = {
      REORDER_REQUESTED: 'Reorder Deliverables',
      REASSIGN_ALL_REQUESTS: 'Reassign All Deliverables',
      REASSIGN_TICKET_COMPLETE: 'Reassign (Ticket Complete)',
      REASSIGN_REQUEST_COMPLETE: 'Reassign (Deliverable Complete)',
    }
    return labels[motive] || motive
  }

  if (isLoading && !monitoringData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800">Error loading queue monitoring data</p>
      </div>
    )
  }

  const { metrics, activeQueues, processingStats } = monitoringData || {
    metrics: {
      totalQueues: 0,
      queued: 0,
      started: 0,
      finished: 0,
      crashed: 0,
      rejected: 0,
      cancelled: 0,
    },
    activeQueues: [],
    processingStats: { byProject: [], byOrganization: [], byMotive: [] },
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reassignment Process Monitor</h1>
          <p className="text-gray-600">Track and manage deliverable reordering and reassignment operations in real-time</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Auto-refresh
          </label>
          <button
            onClick={() => refetch()}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <ArrowPathIcon className="h-5 w-5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <RectangleStackIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Total Queues</p>
              <p className="text-2xl font-bold text-gray-900">{metrics.totalQueues}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <ClockIcon className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Queued</p>
              <p className="text-2xl font-bold text-yellow-600">{metrics.queued}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <ArrowPathIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Processing</p>
              <p className="text-2xl font-bold text-blue-600">{metrics.started}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Finished</p>
              <p className="text-2xl font-bold text-green-600">{metrics.finished}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Active Queues */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Active Queues</h2>
          <p className="text-sm text-gray-500">Currently queued or processing</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project / Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Motive
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Movements
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activeQueues.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    <div className="py-8">
                      <RectangleStackIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                      <p className="text-sm font-medium">All queues are idle</p>
                      <p className="text-xs text-gray-400 mt-1">No reassignment processes are currently running</p>
                    </div>
                  </td>
                </tr>
              ) : (
                activeQueues.map((queue) => (
                  <tr
                    key={queue.id}
                    className={queue.status === 'STARTED' ? 'bg-blue-50' : ''}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {queue.project_name}
                      </div>
                      <div className="text-sm text-gray-500">{queue.organization_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getMotiveLabel(queue.motive)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(queue.status)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {queue.duration_seconds ? formatDuration(queue.duration_seconds) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {queue.changes_count || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {queue.status === 'STARTED' && (
                        <button
                          onClick={() => handleCancelQueue(queue.id)}
                          disabled={isCancelling}
                          className="text-red-600 hover:text-red-900 flex items-center gap-1"
                        >
                          <StopIcon className="h-4 w-4" />
                          Stop
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Processing Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Project */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Top Projects (Last 7 Days)</h2>
            <p className="text-sm text-gray-500">Most active projects by queue processing</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {processingStats.byProject.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">No processing activity in the last 7 days</p>
                </div>
              ) : (
                processingStats.byProject.slice(0, 10).map((project) => (
                  <div key={project.project_id} className="border-b border-gray-100 pb-4 last:border-0">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{project.project_name}</p>
                        <p className="text-xs text-gray-500">{project.organization_name}</p>
                      </div>
                      <span className="text-sm font-semibold text-blue-600">
                        {project.queues_processed}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs text-gray-500">
                      <div>
                        <span className="font-medium">Movements:</span> {project.total_movements}
                      </div>
                      <div>
                        <span className="font-medium">Avg Time:</span>{' '}
                        {formatDuration(Math.round(project.avg_duration_seconds))}
                      </div>
                      <div>
                        <span className="font-medium">Last:</span>{' '}
                        {formatDateTimeEST(project.last_processed)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* By Organization */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Top Organizations (Last 7 Days)</h2>
            <p className="text-sm text-gray-500">Most active organizations by queue processing</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {processingStats.byOrganization.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">No processing activity in the last 7 days</p>
                </div>
              ) : (
                processingStats.byOrganization.slice(0, 10).map((org) => (
                  <div key={org.organization_id} className="border-b border-gray-100 pb-4 last:border-0">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{org.organization_name}</p>
                        <p className="text-xs text-gray-500">{org.projects_count} project(s)</p>
                      </div>
                      <span className="text-sm font-semibold text-blue-600">
                        {org.queues_processed}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                      <div>
                        <span className="font-medium">Movements:</span> {org.total_movements}
                      </div>
                      <div>
                        <span className="font-medium">Avg Time:</span>{' '}
                        {formatDuration(Math.round(org.avg_duration_seconds))}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* By Motive */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Queue Types (Last 7 Days)</h2>
          <p className="text-sm text-gray-500">Breakdown by queue motive/type</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {processingStats.byMotive.map((motive) => (
              <div
                key={motive.motive}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <p className="text-sm font-medium text-gray-900 mb-2">
                  {getMotiveLabel(motive.motive)}
                </p>
                <div className="space-y-1 text-xs text-gray-500">
                  <div className="flex justify-between">
                    <span>Count:</span>
                    <span className="font-semibold text-gray-900">{motive.count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Avg Duration:</span>
                    <span className="font-semibold text-gray-900">
                      {formatDuration(Math.round(motive.avg_duration_seconds))}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default QueueMonitoring
